import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const JobDescriptionUpload = ({ onJobParsed, isProcessing }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [pastedText, setPastedText] = useState('');
  const [showTextArea, setShowTextArea] = useState(false);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = (file) => {
    if (!file) return;

    const validTypes = ['text/plain', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    
    if (!validTypes?.includes(file?.type) && !file?.name?.endsWith('.txt')) {
      alert('Please upload a text file (.txt, .pdf, .doc, or .docx)');
      return;
    }

    setUploadedFileName(file?.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event?.target?.result;
      onJobParsed(text);
    };
    reader?.readAsText(file);
  };

  const handlePasteSubmit = () => {
    if (pastedText?.trim()) {
      onJobParsed(pastedText);
      setUploadedFileName('Pasted job description');
    }
  };

  const handleClear = () => {
    setUploadedFileName(null);
    setPastedText('');
    setShowTextArea(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Sparkles" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">AI-Powered Candidate Recommendations</h3>
        </div>
        {uploadedFileName && (
          <Button
            variant="outline"
            size="sm"
            iconName="X"
            iconPosition="left"
            onClick={handleClear}
          >
            Clear
          </Button>
        )}
      </div>

      <p className="text-sm text-[#6B7280] mb-4">
        Upload a job description and let AI find the best matching candidates for you.
      </p>

      {!uploadedFileName ? (
        <>
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              isDragging
                ? 'border-[#5B2DFF] bg-[#5B2DFF]/5'
                : 'border-[#E5E7EB] hover:border-[#5B2DFF]/50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 bg-[#5B2DFF]/10 rounded-full flex items-center justify-center">
                <Icon name="Upload" size={24} color="#5B2DFF" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#111827] mb-1">
                  Drag & drop your job description here
                </p>
                <p className="text-xs text-[#6B7280]">
                  Supports .txt, .pdf, .doc, .docx files
                </p>
              </div>
              <div className="flex items-center gap-3 w-full">
                <div className="flex-1 h-px bg-[#E5E7EB]"></div>
                <span className="text-xs text-[#6B7280]">or</span>
                <div className="flex-1 h-px bg-[#E5E7EB]"></div>
              </div>
              <div className="flex gap-2">
                <label>
                  <input
                    type="file"
                    className="hidden"
                    accept=".txt,.pdf,.doc,.docx"
                    onChange={handleFileSelect}
                  />
                  <Button
                    variant="default"
                    size="sm"
                    iconName="FileText"
                    iconPosition="left"
                    as="span"
                  >
                    Browse Files
                  </Button>
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ClipboardPaste"
                  iconPosition="left"
                  onClick={() => setShowTextArea(!showTextArea)}
                >
                  Paste Text
                </Button>
              </div>
            </div>
          </div>

          {showTextArea && (
            <div className="mt-4">
              <textarea
                className="w-full h-32 px-4 py-3 border border-[#E5E7EB] rounded-xl text-sm text-[#111827] placeholder:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#5B2DFF] focus:border-transparent resize-none"
                placeholder="Paste your job description here..."
                value={pastedText}
                onChange={(e) => setPastedText(e?.target?.value)}
              />
              <div className="flex justify-end mt-2">
                <Button
                  variant="default"
                  size="sm"
                  iconName="Sparkles"
                  iconPosition="left"
                  onClick={handlePasteSubmit}
                  disabled={!pastedText?.trim()}
                >
                  Analyze Job Description
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-[#F6F7FB] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#10B981]/10 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle2" size={20} color="#10B981" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-[#111827]">{uploadedFileName}</p>
              <p className="text-xs text-[#6B7280]">
                {isProcessing ? 'Analyzing job description...' : 'Job description analyzed successfully'}
              </p>
            </div>
            {isProcessing && (
              <div className="animate-spin">
                <Icon name="Loader2" size={20} color="#5B2DFF" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDescriptionUpload;